/**
 * useFormNavigation.js
 * Custom Hook para navegación estilo desktop
 * Zero Hardcode - Modular y reutilizable
 */

import { useEffect, useRef, useCallback } from 'react';
import { 
  getNavigableFields, 
  getNextFieldIndex, 
  isLastField, 
  getFirstField 
} from '../utils/formNavigation';

/**
 * Hook para manejar navegación con Enter y auto-focus
 * 
 * @param {Array} fields - Schema fields
 * @param {Object} formData - Datos del formulario
 * @param {boolean} isReadOnly - Si el formulario es solo lectura
 * @param {Function} onSubmit - Callback para submit
 * @returns {object} - Refs y handlers
 */
export function useFormNavigation(fields, formData, isReadOnly) {
  
  // Refs para los inputs
  const inputRefs = useRef({});

  // ==========================================
  // AUTO-FOCUS EN PRIMER CAMPO (SOLO UNA VEZ)
  // ==========================================
  
  const hasFocusedRef = useRef(false);
  
  useEffect(() => {
    // Solo ejecutar una vez
    if (hasFocusedRef.current) return;
    if (isReadOnly || !fields || fields.length === 0) return;

    // Pequeño delay para que el modal termine de renderizar
    const timer = setTimeout(() => {
      const navigableFields = getNavigableFields(fields, formData);
      const firstField = getFirstField(navigableFields);
      
      if (firstField && inputRefs.current[firstField]) {
        inputRefs.current[firstField].focus();
        hasFocusedRef.current = true;  // Marcar como ejecutado
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // 🔥 Array vacío = solo al montar

  // ==========================================
  // NAVEGAR AL SIGUIENTE CAMPO
  // ==========================================
  
  const navigateToNextField = useCallback((currentFieldName) => {
    const navigableFields = getNavigableFields(fields, formData);
    const nextIndex = getNextFieldIndex(navigableFields, currentFieldName);

    if (nextIndex === -1) {
      // Es el último campo → Retornar true para indicar submit
      return true;
    } else {
      // Ir al siguiente campo
      const nextFieldName = navigableFields[nextIndex];
      if (inputRefs.current[nextFieldName]) {
        inputRefs.current[nextFieldName].focus();
      }
      return false;
    }
  }, [fields, formData]);

  // ==========================================
  // HANDLER DE ENTER
  // ==========================================
  
  const handleKeyDown = useCallback((e, fieldName, fieldType) => {
    // Solo procesar Enter
    if (e.key !== 'Enter') return;

    // No procesar si es textarea
    if (fieldType === 'textarea') return;

    // Prevenir submit default del form
    e.preventDefault();

    // Navegar al siguiente campo
    const shouldSubmit = navigateToNextField(fieldName);
    
    // Retornar si debe hacer submit
    return shouldSubmit;
  }, [navigateToNextField]);

  // ==========================================
  // REGISTRAR REF
  // ==========================================
  
  const registerRef = useCallback((fieldName, ref) => {
    if (ref) {
      inputRefs.current[fieldName] = ref;
    }
  }, []);

  // ==========================================
  // RETURN
  // ==========================================
  
  return {
    registerRef,      // (fieldName, ref) => void
    handleKeyDown,    // (e, fieldName, fieldType) => void
    inputRefs         // { fieldName: ref }
  };
}